<template>
  <div class="am-vehicle-player-gallery-stage" :class="classes">
    <div class="am-vehicle-player-gallery-stage__items-wrapper" ref="boundary">
      <div class="am-vehicle-player-gallery-stage__items" ref="items">
        <template v-for="(item, i) in enrichedItems">
          <am-vehicle-player-image-stage
            ref="item"
            :key="'image-' + i"
            v-if="item.data.type === 'image'"
            :source="item.data.imageSource"
            :alt="item.data.alt"
            :data-id="item.id"
            class="am-vehicle-player-gallery-stage__item"
          ></am-vehicle-player-image-stage>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { getImageQualityParams } from './../../../../../../../../utils/image'
import AmVehiclePlayerImageStage from './components/image/vehicle-player-image-stage.vue'

export default {
  name: 'AmVehiclePlayerGalleryStage',

  components: {
    AmVehiclePlayerImageStage
  },

  model: {
    event: 'change',
    prop: 'value'
  },

  props: {
    items: {
      type: Array,
      required: false,
      default: () => []
    },
    value: {
      type: String,
      required: false,
      default: null
    }
  },

  data() {
    return {
      resizeObserverInstance: null,
      intersectionObserverInstance: null,
      width: null,
      selectedItemId: null,
      timer: null,
      isDragging: false,
      dragStartX: 0,
      scrollLeft: 0
    }
  },

  computed: {
    classes() {
      return {
        'am-vehicle-player-gallery-stage--dragging': this.isDragging
      }
    },
    enrichedItems() {
      return this.items.map(item => {
        let resolvedItem

        if (item.type === 'gallery' && item.data.type === 'image') {
          resolvedItem = Object.assign({}, item, {
            data: Object.assign({}, item.data, {
              imageSource:
                this.width !== null
                  ? this.getResolvedImageSource(item.data.source, this.width)
                  : null
            }) // TODO how to handle window undefined in future?
          })
        } else {
          resolvedItem = item
        }

        return resolvedItem
      })
    }
  },

  watch: {
    value() {
      // TODO: needed?
      this.debounce(this.scrollToActiveItem, 250)
    }
  },

  mounted() {
    this.initResizeObserver()
    this.initIntersectionObserver()
    this.addMouseListener()
  },

  beforeDestroy() {
    this.resizeObserverInstance && this.resizeObserverInstance.disconnect()
    this.intersectionObserverInstance &&
      this.intersectionObserverInstance.disconnect()
    this.removeMouseListener()
  },

  methods: {
    addMouseListener() {
      const slider = this.$refs.items
      slider.addEventListener('mousedown', this.mouseDown)
      slider.addEventListener('mouseleave', this.mouseLeave)
      slider.addEventListener('mouseup', this.mouseUp)
      slider.addEventListener('mousemove', this.mouseMove)
    },
    removeMouseListener() {
      const slider = this.$refs.items
      slider.removeEventListener('mousedown', this.mouseDown)
      slider.removeEventListener('mouseleave', this.mouseLeave)
      slider.removeEventListener('mouseup', this.mouseUp)
      slider.removeEventListener('mousemove', this.mouseMove)
    },
    debounce(f, delay) {
      if (this.timer) {
        clearTimeout(this.timer)
      }
      this.timer = setTimeout(() => f.apply(this), delay)
    },
    initResizeObserver() {
      this.resizeObserverInstance = new ResizeObserver(entries => {
        for (const entry of entries) {
          if (entry.contentBoxSize) {
            const width = this.$el.offsetWidth
            if (width > this.width) {
              this.width = width
            }
          }
        }
      })

      this.resizeObserverInstance.observe(this.$el)
    },
    initIntersectionObserver() {
      const options = {
        root: this.$refs.boundary,
        threshold: 0.5
      }

      this.intersectionObserverInstance = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.selectedItemId = entry.target.dataset.id
            this.$emit('change', entry.target.dataset.id)
          }
        })
      }, options)

      this.$refs.item.forEach(itemRef => {
        this.intersectionObserverInstance.observe(itemRef.$el)
      })
    },
    getResolvedImageSource(source, width) {
      const { pixelRatio } = getImageQualityParams()
      const splittedSegments = source.split('/')
      const filename = splittedSegments.pop()
      const resolvedSource =
        splittedSegments.join('/') +
        '/tr:w-' +
        width * pixelRatio +
        ',h-' +
        ((width * 2) / 3) * pixelRatio +
        ',fo-auto/' +
        filename

      return resolvedSource
    },
    scrollToActiveItem() {
      if (this.value !== this.selectedItemId) {
        const activeIndex = this.items.findIndex(item => item.id === this.value)
        this.$refs.items.scrollTo({
          left: activeIndex * this.$refs.boundary.offsetWidth,
          top: 0,
          behavior: 'smooth'
        })
      }
    },
    mouseDown(e) {
      const slider = this.$refs.items
      this.isDragging = true
      this.dragStartX = e.pageX - slider.offsetLeft
      this.scrollLeft = slider.scrollLeft
    },
    mouseLeave() {
      this.isDragging = false
    },
    mouseUp() {
      this.isDragging = false
    },
    mouseMove(e) {
      if (this.isDragging) {
        e.preventDefault()
        const slider = this.$refs.items
        const left =
          this.scrollLeft - (e.pageX - slider.offsetLeft - this.dragStartX)
        slider.scrollTo({
          left,
          behavior: 'smooth'
        })
      }
    }
  }
}
</script>
